import { useEffect, useState, memo } from 'react'
import he from 'he'
import { useForm } from 'react-hook-form'
import { contentAPI } from '../services/api'
import { Eye, EyeOff, Trash2, Reply, Save, MessageSquare, Star, AlertCircle, Calendar, Filter, RefreshCw, ChevronDown } from 'lucide-react'
import toast from 'react-hot-toast'
import LoadingSpinner from '../components/LoadingSpinner'

const KotakSaran = () => {
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [saving, setSaving] = useState(false)
  const [feedbacks, setFeedbacks] = useState([])
  const [replyingTo, setReplyingTo] = useState(null)
  const [error, setError] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [totalCount, setTotalCount] = useState(0)
  
  // Filter states
  const [selectedType, setSelectedType] = useState('')
  const [selectedTimeRange, setSelectedTimeRange] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [sortOption, setSortOption] = useState('terbaru')

  const API_URL = import.meta.env.VITE_API_URL
  const ITEMS_PER_PAGE = 10

  const timeRangeOptions = [
    { value: '', label: 'Semua Waktu' },
    { value: 'today', label: 'Hari Ini' },
    { value: 'thisMonth', label: 'Bulan Ini' },
    { value: 'last3Months', label: '3 Bulan Terakhir' },
    { value: 'thisYear', label: 'Tahun Ini' }
  ]

  const typeOptions = [
    { value: '', label: 'Semua Jenis' },
    { value: 'kritik', label: 'Kritik' },
    { value: 'saran', label: 'Saran' },
    { value: 'apresiasi', label: 'Apresiasi' }
  ]

  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm()

  useEffect(() => {
    fetchFeedbacks(1, true)
  }, [selectedType, selectedTimeRange])

  const fetchFeedbacks = async (page = 1, isInitial = false) => {
    try {
      if (isInitial) {
        setLoading(true)
        setError('')
      } else {
        setLoadingMore(true)
      }

      const params = new URLSearchParams({
        page: page.toString(),
        limit: ITEMS_PER_PAGE.toString(),
        ...(selectedType && { type: selectedType }),
        ...(selectedTimeRange && { timeRange: selectedTimeRange })
      })

      const response = await fetch(`${API_URL}/kotak-saran/admin?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (!response.ok) {
        throw new Error('Failed to fetch feedbacks')
      }
      
      const data = await response.json()
      
      if (isInitial) {
        setFeedbacks(data.data || [])
      } else {
        setFeedbacks(prev => [...prev, ...(data.data || [])])
      }
      
      setHasMore(data.pagination?.hasMore || false)
      setTotalCount(data.pagination?.total || 0)
      setCurrentPage(page)
      
    } catch (error) {
      console.error('Error fetching feedbacks:', error)
      setError('Gagal memuat feedback')
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchFeedbacks(currentPage + 1, false)
    }
  }

  const handleRefresh = () => {
    setCurrentPage(1)
    setFeedbacks([])
    fetchFeedbacks(1, true)
  }

  const handleFilterChange = (filterType, value) => {
    if (filterType === 'type') {
      setSelectedType(value)
    } else if (filterType === 'timeRange') {
      setSelectedTimeRange(value)
    }
    setCurrentPage(1)
    setFeedbacks([])
  }

  const updateFeedback = async (id, updateData) => {
    try {
      await contentAPI.updateFeedback(id, updateData)
      
      // Update local state instead of refetching all
      setFeedbacks(prev => 
        prev.map(feedback => 
          feedback._id === id 
            ? { ...feedback, ...updateData }
            : feedback
        )
      )
      return true
    } catch (error) {
      console.error('Error updating feedback:', error)
      toast.error('Gagal memperbarui feedback')
      return false
    }
  }

  const deleteFeedback = async (id) => {
    if (!window.confirm('Yakin ingin menghapus feedback ini?')) return
    try {
      await contentAPI.deleteFeedback(id)
      toast.success('Feedback berhasil dihapus')
      setFeedbacks(prev => prev.filter(feedback => feedback._id !== id))
      setTotalCount(prev => prev - 1)
    } catch (error) {
      console.error('Error deleting feedback:', error)
      toast.error('Gagal menghapus feedback')
    }
  }

  const toggleVisibility = async (id, isVisible) => {
    const success = await updateFeedback(id, { isVisible: !isVisible })
    if (success) {
      toast.success(`Feedback ${!isVisible ? 'ditampilkan' : 'disembunyikan'}`)
    }
  }

  const onReply = async (data) => {
    if (!replyingTo) return
    setSaving(true)
    try {
      const success = await updateFeedback(replyingTo, {
        reply: data.reply,
        repliedAt: new Date().toISOString()
      })
      if (success) {
        toast.success('Balasan berhasil disimpan')
        setReplyingTo(null)
        reset()
      }
    } catch (error) {
      toast.error('Gagal menyimpan balasan')
    } finally {
      setSaving(false)
    }
  }

  const getTypeIcon = (type) => {
    switch (type) {
      case 'kritik':
        return <AlertCircle className="w-5 h-5 text-red-500" />
      case 'saran':
        return <MessageSquare className="w-5 h-5 text-green-500" />
      case 'apresiasi':
        return <Star className="w-5 h-5 text-yellow-500" />
      default:
        return <MessageSquare className="w-5 h-5 text-gray-500" />
    }
  }

  const getTypeColor = (type) => {
    switch (type) {
      case 'kritik':
        return 'bg-red-100 text-red-800 border-red-200'
      case 'saran':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'apresiasi':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getActiveFiltersCount = () => {
    let count = 0
    if (selectedType) count++
    if (selectedTimeRange) count++
    return count
  }

  const sortedFeedbacks = [...feedbacks].sort((a, b) => {
    switch (sortOption) {
      case 'terlama':
        return new Date(a.createdAt) - new Date(b.createdAt)
      case 'sudah-dibalas':
        return a.reply ? -1 : 1
      case 'belum-dibalas':
        return a.reply ? 1 : -1
      case 'terbaru':
      default:
        return new Date(b.createdAt) - new Date(a.createdAt)
    }
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="card">
        <div className="text-center text-red-600 py-8">
          <AlertCircle className="w-12 h-12 mx-auto mb-4" />
          <p className="mb-4">{error}</p>
          <button
            onClick={handleRefresh}
            className="btn btn-primary"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Coba Lagi
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header dekstop*/}
      <div className="hidden lg:block">
        <div className='flex items-center justify-between mb-4'>
          <div>
            <h1 className="header-page">Kotak Saran</h1>
            <p className="description-page">
              Menampilkan {feedbacks.length} dari {totalCount} pesan dari kotak saran
            </p>
          </div>

          <div className="flex items-center space-x-2">
            <select
              className="border border-gray-300 rounded lg:px-3 px-1 py-1 lg:text-sm text-xs"
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
            >
              <option value="terbaru">Terbaru</option>
              <option value="terlama">Terlama</option>
              <option value="sudah-dibalas">Sudah Dibalas</option>
              <option value="belum-dibalas">Belum Dibalas</option>
            </select>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`relative p-2 rounded-full transition-colors ${
                showFilters ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              }`}
              title="Filter"
            >
              <Filter className="w-4 h-4" />
              {getActiveFiltersCount() > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {getActiveFiltersCount()}
                </span>
              )}
            </button>

            <button
              onClick={handleRefresh}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Header mobile */}
      <div className="block lg:hidden">
          <div>
            <h1 className="header-page">Kotak Saran</h1>
          </div>
        <div className='flex items-center justify-between mb-4'>

          <div className="flex items-center space-x-2">
            <select
              className="border border-gray-300 rounded lg:px-3 px-1 py-1 lg:text-sm text-xs"
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
            >
              <option value="terbaru">Terbaru</option>
              <option value="terlama">Terlama</option>
              <option value="sudah-dibalas">Sudah Dibalas</option>
              <option value="belum-dibalas">Belum Dibalas</option>
            </select>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`relative p-2 rounded-full transition-colors ${
                showFilters ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              }`}
              title="Filter"
            >
              <Filter className="w-4 h-4" />
              {getActiveFiltersCount() > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {getActiveFiltersCount()}
                </span>
              )}
            </button>

            <button
              onClick={handleRefresh}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
            <p className="description-page">
              Menampilkan {feedbacks.length} dari {totalCount} pesan dari kotak saran
            </p>
      </div>


      {/* Filters */}
      {showFilters && (
        <div className="card mb-6">
          <div className="card-content">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:p-2 lg:pt-6 pt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 ">
                  Filter Waktu
                </label>  
                <select
                  value={selectedTimeRange}
                  onChange={(e) => handleFilterChange('timeRange', e.target.value)}
                  className="input"
                >
                  {timeRangeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 ">
                  Filter Jenis
                </label>
                <select
                  value={selectedType}
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                  className="input"
                >
                  {typeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Clear Filters */}
            {(selectedType || selectedTimeRange) && (
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => {
                    setSelectedType('')
                    setSelectedTimeRange('')
                  }}
                  className="btn btn-secondary"
                >
                  Hapus Filter
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Feedback List */}
      <div className="card">
        <div className="card-content">
          {sortedFeedbacks.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500">
                {(selectedType || selectedTimeRange) 
                  ? 'Tidak ada feedback yang sesuai dengan filter' 
                  : 'Belum ada feedback yang diberikan'
                }
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {sortedFeedbacks.map((fb, index) => (
                <div key={`${fb._id}-${index}`} className="py-4">
                  <div className="flex justify-between">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center space-x-2 mb-2">
                        {getTypeIcon(fb.type)}
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getTypeColor(fb.type)}`}>
                          {fb.type?.toUpperCase()}
                        </span>
                        <div className="lg:block hidden">
                          {!fb.isVisible && (
                            <span className="px-2 py-1 bg-gray-200 text-gray-600 text-xs rounded-full">
                              Tersembunyi
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="lg:hidden block">
                          {!fb.isVisible && (
                            <span className="px-2 py-1 bg-gray-200 text-gray-600 text-xs rounded-full">
                              Tersembunyi
                            </span>
                          )}
                        </div>
                      <div className="flex items-center text-gray-500 lg:text-sm text-xs">
                          <Calendar className="w-4 h-4 mr-1" />
                          {new Date(fb.createdAt).toLocaleDateString('id-ID', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      <p className="text-gray-700 lg:text-base text-sm mt-1">{he.decode(fb.feedback)}</p>
                    </div>
                    <div className="flex items-center space-x-2 lg:gap-0 gap-1 ml-4">
                      <button
                        onClick={() => toggleVisibility(fb._id, fb.isVisible)}
                        className={`p-2 rounded-full transition-colors ${
                          fb.isVisible
                            ? 'text-green-600 hover:bg-green-100'
                            : 'text-gray-400 hover:bg-gray-100'
                        }`}
                        title={fb.isVisible ? 'Sembunyikan' : 'Tampilkan'}
                      >
                        {fb.isVisible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => deleteFeedback(fb._id)}
                        className="text-red-500 hover:bg-red-50 p-2 rounded-full transition-colors"
                        title="Hapus"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {fb.reply && (
                    <div className="bg-teal-50 mt-4 p-3 rounded border-l-4 border-teal-400">
                      <div className="flex items-center gap-2 text-teal-700">
                        <Reply className="w-4 h-4" />
                        <span className="lg:text-sm text-xs font-medium">Balasan Admin</span>
                      </div>
                      <p className="mt-1 lg:text-base text-sm text-teal-800">{fb.reply}</p>
                      {fb.repliedAt && (
                        <p className="text-xs text-teal-500 mt-1 italic">
                          Dibalas pada: {new Date(fb.repliedAt).toLocaleDateString('id-ID', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      )}
                    </div>
                  )}

                  <div className="pt-4">
                    {replyingTo === fb._id ? (
                      <div className="space-y-4">
                        <textarea
                          {...register('reply', { required: 'Balasan tidak boleh kosong' })}
                          defaultValue={fb.reply || ''}
                          rows={3}
                          className="input"
                          placeholder="Tulis balasan..."
                        />
                        {errors.reply && (
                          <p className="text-red-500 text-sm">{errors.reply.message}</p>
                        )}
                        <div className="flex gap-2">
                          <button
                            onClick={handleSubmit(onReply)}
                            disabled={saving}
                            className="btn btn-primary"
                          >
                            {saving ? <LoadingSpinner size="sm" /> : <Save className="w-4 h-4" />}
                            {saving ? 'Menyimpan...' : 'Simpan'}
                          </button>
                          <button
                            onClick={() => {
                              setReplyingTo(null)
                              reset()
                            }}
                            className="btn btn-secondary"
                          >
                            Batal
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setReplyingTo(fb._id)}
                        className="text-teal-600 flex items-center space-x-1 hover:underline mt-2"
                      >
                        <Reply className="w-4 h-4" />
                        <span className="lg:text-base text-xs">{fb.reply ? 'Edit Balasan' : 'Balas'}</span>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Load More Button */}
          {hasMore && sortedFeedbacks.length > 0 && (
            <div className="text-center mt-6 pt-4 border-t">
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="btn btn-primary"
              >
                {loadingMore ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Memuat...
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4 mr-2" />
                    Lihat Lebih Banyak
                  </>
                )}
              </button>
            </div>
          )}

          {/* End of List Message */}
          {!hasMore && sortedFeedbacks.length > 0 && (
            <div className="text-center mt-6 pt-4 border-t">
              <p className="text-sm text-gray-500">
                Semua feedback telah ditampilkan ({totalCount} total)
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default memo(KotakSaran)